import type {Mapper} from '@automapper/core';
import {type DownloadColumn, type IDownloadByLinkService, ServerResponseStatus} from '@igp/shared/api';
import {map} from '@igp/shared/otel';
import {
    type DownloadStrategyRequest,
    type DownloadStrategyResponse,
    EntityDownloadClientStrategy,
    type IDownloadStrategy,
} from '@igp/shared/redux/download';
import {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {repaymentLogMappingKeys} from './mapper';
import type {RepaymentLogService} from './service';
import {type RepaymentLogDownloadFields, RepaymentLogServiceTypes, type RepaymentLogViewModelKeys} from './types';

@injectable()
export class RepaymentLogDownloadClientStrategy extends EntityDownloadClientStrategy<RepaymentLogViewModelKeys, EntityType> {
    constructor(@inject(ServiceTypes.AutoMapper) mapper: Mapper, @inject(RepaymentLogServiceTypes.service) service: RepaymentLogService) {
        super(mapper, service, EntityType.RepaymentLog, {
            viewModelKeys: repaymentLogMappingKeys.viewModelsKeys,
            queryFields: repaymentLogMappingKeys.queryFileds,
            model: repaymentLogMappingKeys.model,
            downloadModel: repaymentLogMappingKeys.downloadModel,
        });
    }
}

@injectable()
export class RepaymentLogDownloadServerStrategy implements IDownloadStrategy<RepaymentLogViewModelKeys> {
    private readonly _mapper: Mapper;
    private readonly _service: IDownloadByLinkService<RepaymentLogDownloadFields>;

    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(RepaymentLogServiceTypes.service)
        service: IDownloadByLinkService<RepaymentLogDownloadFields>
    ) {
        this._mapper = mapper;
        this._service = service;
    }

    process(request: DownloadStrategyRequest<RepaymentLogViewModelKeys>): Observable<DownloadStrategyResponse<RepaymentLogViewModelKeys>> {
        const columns: DownloadColumn<RepaymentLogDownloadFields>[] = request.keys?.map<DownloadColumn<RepaymentLogDownloadFields>>(k => {
            const queryField = this._mapper.map<RepaymentLogViewModelKeys, RepaymentLogDownloadFields>(
                k,
                repaymentLogMappingKeys.viewModelsKeys,
                repaymentLogMappingKeys.downloadFields
            );
            return {
                name: request.headers[k] ?? k,
                field: queryField,
            };
        });
        return this._service
            .getDownloadLink({
                filter: request.filter,
                columns,
                report_name: request.filename,
            })
            .pipe(
                map(response => ({
                    status: response?.status === ServerResponseStatus.Success ? 'Success' : 'Failed',
                    link: response?.responsePayload,
                    mode: 'server',
                }))
            );
    }
}
